/**
 * useRum — Real User Monitoring hook for Core Web Vitals.
 *
 * Registers web-vitals callbacks (CLS, INP, LCP, FCP, TTFB) and ships
 * each metric event to /api/rum via navigator.sendBeacon (preferred) or
 * fetch with keepalive (fallback).
 *
 * Payload shape per FR-026:
 *   { name, value, id, route, viewport, ts }
 *
 * Viewport classification:
 *   - width < 768  -> "mobile"
 *   - 768..1023    -> "tablet"
 *   - width >= 1024 -> "desktop"
 *
 * Authored by T320 (FR-026).
 */

import { useEffect, useRef } from "react";
import { onCLS, onINP, onLCP, onFCP, onTTFB } from "web-vitals";

/** RUM ingestion endpoint path. */
const RUM_ENDPOINT = "/api/rum";

/** Viewport classification breakpoints. */
type ViewportClass = "mobile" | "tablet" | "desktop";

function getViewportClass(): ViewportClass {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

/** Payload shape shipped to the RUM endpoint. */
interface RumEvent {
  name: string;
  value: number;
  id: string;
  route: string;
  viewport: ViewportClass;
  ts: number;
}

/**
 * Ship a single RUM event to the ingestion endpoint.
 *
 * Prefers navigator.sendBeacon for reliability during page unload.
 * Falls back to fetch with keepalive when sendBeacon is unavailable.
 */
function shipEvent(event: RumEvent): void {
  const body = JSON.stringify(event);

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    navigator.sendBeacon(RUM_ENDPOINT, body);
    return;
  }

  // Fallback: fetch with keepalive
  void fetch(RUM_ENDPOINT, {
    method: "POST",
    keepalive: true,
    body,
    headers: { "Content-Type": "application/json" },
  });
}

/**
 * useRum — mount once per route to collect Core Web Vitals.
 *
 * @param route - The current route path (e.g., "/today", "/workspaces").
 */
export function useRum(route: string): void {
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    /** Guard: only ship if the hook is still mounted. */
    function handleMetric(metric: { name: string; value: number; id: string }): void {
      if (!mountedRef.current) return;

      const event: RumEvent = {
        name: metric.name,
        value: metric.value,
        id: metric.id,
        route,
        viewport: getViewportClass(),
        ts: Date.now(),
      };

      shipEvent(event);
    }

    onCLS(handleMetric);
    onINP(handleMetric);
    onLCP(handleMetric);
    onFCP(handleMetric);
    onTTFB(handleMetric);

    return () => {
      mountedRef.current = false;
    };
  }, [route]);
}
